/**
 * Everything here works on YYYY-MM-DD strings rather than Date objects.
 * A booking date is a calendar date at the office, not an instant, and
 * putting it through a JS Date in the browser's timezone is how it ends
 * up a day out for anyone travelling.
 */

export type IsoDate = string;

export function toIso(date: Date): IsoDate {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function today(): IsoDate {
  return toIso(new Date());
}

function parse(iso: IsoDate): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

export function addDays(iso: IsoDate, days: number): IsoDate {
  const date = parse(iso);
  date.setDate(date.getDate() + days);
  return toIso(date);
}

/** Monday of the week containing the given date. */
export function weekStart(iso: IsoDate): IsoDate {
  const date = parse(iso);
  const offset = (date.getDay() + 6) % 7;
  return addDays(iso, -offset);
}

export function workWeek(iso: IsoDate): IsoDate[] {
  const monday = weekStart(iso);
  return [0, 1, 2, 3, 4].map((n) => addDays(monday, n));
}

const WEEKDAY = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const MONTH = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

export function weekdayName(iso: IsoDate): string {
  return WEEKDAY[(parse(iso).getDay() + 6) % 7] ?? '';
}

export function dayOfMonth(iso: IsoDate): number {
  return parse(iso).getDate();
}

export function monthName(iso: IsoDate): string {
  return MONTH[parse(iso).getMonth()] ?? '';
}

export function isPast(iso: IsoDate): boolean {
  return iso < today();
}

export function describe(iso: IsoDate): string {
  if (iso === today()) return 'Today';
  if (iso === addDays(today(), 1)) return 'Tomorrow';
  return `${weekdayName(iso)} ${dayOfMonth(iso)} ${monthName(iso).slice(0, 3)}`;
}
